import {
  geocodeAutocompleteResponseSchema,
  geocodeParsedAddressSchema,
} from "@chotca/config";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

export function createGeocodeResource(ctx: RequestContext) {
  return {
    /**
     * Parse a Google Maps share URL into structured address + GPS coords.
     * Server-side handles short-link expansion (maps.app.goo.gl) and address
     * extraction from the `/place/...` path segment — no API key required.
     */
    parseUrl(url: string) {
      return requestJson(ctx, {
        method: "POST",
        path: "/geocode/parse-url",
        body: { url },
        dataSchema: geocodeParsedAddressSchema,
        auth: true,
      });
    },

    /**
     * Google Places (New) address autocomplete. Pass the same `sessionToken`
     * (a crypto.randomUUID) for every keystroke of one search and then to
     * placeDetails() — that groups the calls into a single Google billing
     * session. Debounce on the caller side (~400ms) to avoid a request per key.
     */
    autocomplete(input: string, sessionToken?: string) {
      return requestJson(ctx, {
        method: "POST",
        path: "/geocode/autocomplete",
        body: { input, sessionToken },
        dataSchema: geocodeAutocompleteResponseSchema,
        auth: true,
      });
    },

    /**
     * Resolve a place id from `autocomplete` into the same parsed-address shape
     * as `parseUrl` (address / district / city / province / lat / lng / mapUrl).
     * Pass the search's `sessionToken` to close the billing session.
     */
    placeDetails(placeId: string, sessionToken?: string) {
      return requestJson(ctx, {
        method: "POST",
        path: "/geocode/place-details",
        body: { placeId, sessionToken },
        dataSchema: geocodeParsedAddressSchema,
        auth: true,
      });
    },
  };
}
